// src/components/ImageUploader.js
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Box, Typography, CircularProgress, List, ListItem, ListItemText,
  Button, Chip, Stack, Alert,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useT } from "../i18n/LanguageContext";
import CameraScanner from "./CameraScanner";
import axios from "axios";
import { auth } from "./firebaseConfig";
import { useAuthState } from "react-firebase-hooks/auth";

const statusColor = (s) =>
  s === "SUCCESS" ? "success"
    : s === "REVIEW" ? "warning"
      : s === "DUPLICATE" ? "warning"
        : s === "SKIPPED" ? "info"   // an invoice/cover sheet — deliberate, nothing to rescan
          : s === "FAILED" ? "error" : "default";

// Per-page chip colour for the page-by-page breakdown of a multi-page PDF.
const pageColor = (s) =>
  s === "SAVED" ? "success"
    : s === "ERROR" ? "warning"
      : s === "FIELD" ? "secondary"
        : s === "EXPENSE" ? "primary"  // fuel/expense receipt routed to Fuel & Expenses
        : s === "DUPLICATE" ? "info"
          : s === "SKIPPED" ? "default"  // invoice/cover sheet — expected, not a problem
            : "error"; // UNREADABLE

// Client-side downscale before upload: phone photos are often 3000–4000px / several MB, which is
// slower to upload AND slower for Gemini. We cap the long side at MAX_DIM and re-encode as JPEG.
// Conservative on purpose — only genuinely large images are touched (so tiny ticket/truck numbers
// stay legible), non-images (PDFs) pass through untouched, and ANY failure falls back to the original.
const MAX_DIM = 2000;              // long-side cap sent to the OCR
const DOWNSCALE_THRESHOLD = 2400; // leave anything already at/under this alone
const JPEG_QUALITY = 0.85;

const downscaleImage = (file) =>
  new Promise((resolve) => {
    if (!file?.type?.startsWith("image/")) { resolve(file); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const longSide = Math.max(img.width, img.height);
      if (!longSide || longSide <= DOWNSCALE_THRESHOLD) { URL.revokeObjectURL(url); resolve(file); return; }
      try {
        const scale = MAX_DIM / longSide;
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        canvas.toBlob(
          (blob) => {
            if (!blob || blob.size >= file.size) { resolve(file); return; } // no win → keep original
            const name = file.name.replace(/\.(png|jpe?g|webp|heic|heif|bmp|tiff?)$/i, "") + ".jpg";
            resolve(new File([blob], name, { type: "image/jpeg", lastModified: Date.now() }));
          },
          "image/jpeg",
          JPEG_QUALITY
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

const ImageUploader = ({ onUpload }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [user] = useAuthState(auth);
  const t = useT();

  // Per-file scan status. Each: { name, file, status, message }
  const [uploadResults, setUploadResults] = useState([]);

  const uploadOneFile = async (file) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const toSend = await downscaleImage(file); // shrink large photos before upload (falls back to original)
    const formData = new FormData();
    formData.append("file", toSend);
    if (user?.email) formData.append("email", user.email);

    const response = await axios.post(`${apiUrl}/api/invoice/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response?.data; // ScanResultDTO { fileName, status, ticketsExtracted, message }
  };

  // Upsert a result row by file name.
  const setRow = (name, patch, file) =>
    setUploadResults((prev) => {
      const idx = prev.findIndex((r) => r.name === name);
      if (idx === -1) return [...prev, { name, file, status: "UPLOADING", message: "", ...patch }];
      const copy = [...prev];
      copy[idx] = { ...copy[idx], ...patch, file: file ?? copy[idx].file };
      return copy;
    });

  // Scan a single file; records the full per-file breakdown and returns the result for aggregation.
  const scanFile = async (file) => {
    setRow(file.name, { status: "UPLOADING", message: "Scanning…" }, file);
    try {
      const data = await uploadOneFile(file);
      // Common breakdown captured on the row for every status (drives chips + page list).
      const breakdown = {
        // pageCount = how many pages went in (`pages` is the per-page detail array, not a count).
        pageCount: data?.pageCount ?? 0,
        tickets: data?.ticketsExtracted ?? 0,
        errors: data?.errors ?? 0,
        fields: data?.fields ?? 0,
        expenses: data?.expenses ?? 0,
        duplicates: data?.duplicates ?? 0,
        unreadable: data?.unreadable ?? 0,
        skipped: data?.skipped ?? 0,
        batchId: data?.batchId ?? null,
        pages: data?.pages ?? [],
      };
      const status = data?.status || (breakdown.tickets > 0 ? "SUCCESS" : "FAILED");
      setRow(file.name, {
        status,
        message: data?.message || "Scanned",
        ...breakdown,
      });
      // SUCCESS = at least one clean ticket to aggregate upward. REVIEW/DUPLICATE aren't failures
      // (nothing to rescan — REVIEW rows are saved and fixed in the Tickets table); only FAILED is.
      return { success: status === "SUCCESS", data };
    } catch (e) {
      const body = e?.response?.data;
      const msg = (body && (body.message || (typeof body === "string" ? body : null))) || "Scan failed.";
      setRow(file.name, { status: "FAILED", message: msg });
      return { success: false };
    }
  };

  // Scan a batch with a small concurrency pool (was strictly sequential). A drop of N files now runs
  // up to SCAN_CONCURRENCY scans at once, so 10 tickets no longer wait 10 Gemini round-trips end to
  // end — each file still reports its own status independently. Kept modest to stay within the
  // Gemini/App Engine request budget.
  const SCAN_CONCURRENCY = 4;
  const processBatch = async (files) => {
    if (!files || files.length === 0) return;
    setLoading(true);
    setError("");
    const aggregated = [];
    let cursor = 0;
    const worker = async () => {
      while (cursor < files.length) {
        const file = files[cursor++];
        // eslint-disable-next-line no-await-in-loop
        const { success, data } = await scanFile(file);
        if (success) aggregated.push({ fileName: file.name, extractedText: data?.text ?? "", raw: data });
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(SCAN_CONCURRENCY, files.length) }, worker)
    );
    setLoading(false);
    if (onUpload && aggregated.length) onUpload(aggregated);
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      setUploadResults([]);
      await processBatch(acceptedFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onUpload, user]
  );

  const rescanOne = async (row) => {
    if (row.file) await processBatch([row.file]);
  };

  const rescanFailed = async () => {
    const failed = uploadResults.filter((r) => r.status === "FAILED" && r.file).map((r) => r.file);
    await processBatch(failed);
  };

  // Counts FILES that failed outright (drives the Rescan button), NOT pages. Everything below counts
  // PAGES and TICKETS — mixing the two in one chip row is what made "Failed: 0" read as "nothing
  // lost" while a page had in fact been dropped.
  const failedCount = uploadResults.filter((r) => r.status === "FAILED").length;

  // Pages that went in — the denominator. Every page should end up as a ticket, a duplicate skip,
  // or a drop; if pages > tickets + duplicates, something was scanned and lost.
  const pagesTotal = uploadResults.reduce((sum, r) => sum + (r.pageCount || 0), 0);
  // Clean, fully-attributed tickets.
  const ticketsScanned = uploadResults.reduce((sum, r) => sum + (r.tickets || 0), 0);
  // Tickets captured but needing a truck fix (from any file, incl. partially-successful ones).
  const reviewCount = uploadResults.reduce((sum, r) => sum + (r.errors || 0), 0);
  // Rows that actually landed in Tickets = clean + needs-review. (The old "Tickets scanned" chip
  // showed only the clean ones, so a 10-page scan that saved 9 tickets reported "2".)
  const ticketsSaved = ticketsScanned + reviewCount;
  // Pages auto-detected as handwritten field tickets and routed to the Field Tickets screen.
  const fieldCount = uploadResults.reduce((sum, r) => sum + (r.fields || 0), 0);
  // Pages auto-detected as fuel/expense receipts and routed to the Fuel & Expenses screen.
  const expenseCount = uploadResults.reduce((sum, r) => sum + (r.expenses || 0), 0);
  // Pages skipped as already-scanned — deliberate, not a loss.
  const duplicatePages = uploadResults.reduce((sum, r) => sum + (r.duplicates || 0), 0);
  // Pages that produced NOTHING. The one outcome that silently underbills, so it's called out.
  const droppedCount = uploadResults.reduce((sum, r) => sum + (r.unreadable || 0), 0);
  // Invoice/cover sheets deliberately not scanned. Expected in an Ivy's packet (the invoice is page
  // 1), so this is information, NOT a drop — it must never trigger the "produced no ticket" warning.
  const skippedCount = uploadResults.reduce((sum, r) => sum + (r.skipped || 0), 0);

  const handleScanCapture = async (imageDataURL) => {
    setLoading(true);
    setError("");
    try {
      const blob = await fetch(imageDataURL).then((res) => res.blob());
      const file = new File([blob], `scan-${Date.now()}.png`, { type: "image/png" });
      const { success, data } = await scanFile(file);
      if (success && onUpload) {
        onUpload([{ fileName: file.name, extractedText: data?.text ?? "", raw: data }]);
      }
    } catch (err) {
      setError("Error processing the scanned image.");
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true,
    accept: { "image/*": [], "application/pdf": [] },
  });

  // `capture` makes a phone open its camera straight away instead of the file picker; desktop
  // browsers ignore it. Same batch path as the dropzone, so status/rescan behave identically.
  const onCameraPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow photographing the next ticket with the same input
    if (!files.length) return;
    setUploadResults([]);
    await processBatch(files);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: 'auto' }}>
      {!isScanning && (
        <Box>
          <Typography variant="h5" gutterBottom>{t("scan.title")}</Typography>

          {/* Phones: two big buttons, camera first. The drag-and-drop box is a desktop idiom. */}
          <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" }, mt: 1 }}>
            <Button
              component="label"
              variant="contained"
              size="large"
              startIcon={<PhotoCameraIcon />}
              disabled={loading}
              sx={{ py: 2, fontSize: 18 }}
            >
              {t("scan.takePhoto")}
              <input hidden type="file" accept="image/*" capture="environment" onChange={onCameraPick} />
            </Button>
            <Button
              component="label"
              variant="outlined"
              size="large"
              startIcon={<UploadFileIcon />}
              disabled={loading}
              sx={{ py: 1.5, fontSize: 16 }}
            >
              {t("scan.chooseFiles")}
              <input hidden type="file" accept="image/*,application/pdf" multiple onChange={onCameraPick} />
            </Button>
            <Typography variant="body2" color="text.secondary">{t("scan.phoneHelp")}</Typography>
          </Stack>

          <Box
            {...getRootProps()}
            sx={{
              display: { xs: "none", md: "block" },
              marginTop: "8px",
              border: "2px dashed gray",
              borderRadius: 1,
              padding: "30px",
              cursor: "pointer",
            }}
          >
            <input {...getInputProps()} />
            <Typography variant="h6">{t("scan.dropzone")}</Typography>
            <Typography variant="body2">{t("scan.dropzoneHint")}</Typography>
          </Box>

          <Box sx={{ mt: 1, display: { xs: "none", md: "block" } }}>
            <Typography variant="body2" color="text.secondary">
              {t("scan.help")}
            </Typography>
          </Box>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", mt: 4, gap: 1 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">{t("scan.scanning")}</Typography>
        </Box>
      )}
      {error && <Typography color="error">{error}</Typography>}

      {uploadResults.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
            <Typography variant="subtitle1">Scan Status</Typography>
            {/* Lead with pages-in -> tickets-out. Every page should become a ticket or a deliberate
                skip; showing the denominator is what makes a lost page visible at a glance. */}
            {pagesTotal > 0 && (
              <Chip
                size="small"
                color={droppedCount > 0 ? "error" : "primary"}
                label={`${pagesTotal} page${pagesTotal === 1 ? "" : "s"} → ${ticketsSaved} ticket${ticketsSaved === 1 ? "" : "s"}`}
              />
            )}
            {ticketsScanned > 0 && (
              <Chip size="small" color="success" variant="outlined" label={`Clean: ${ticketsScanned}`} />
            )}
            {reviewCount > 0 && (
              <Chip size="small" color="warning" variant="outlined" label={`Needs review: ${reviewCount}`} />
            )}
            {fieldCount > 0 && (
              <Chip size="small" color="secondary" variant="outlined" label={`Field (review): ${fieldCount}`} />
            )}
            {expenseCount > 0 && (
              <Chip size="small" color="primary" variant="outlined" label={`Fuel/expense: ${expenseCount}`} />
            )}
            {duplicatePages > 0 && (
              <Chip size="small" color="info" variant="outlined" label={`Duplicates: ${duplicatePages}`} />
            )}
            {skippedCount > 0 && (
              <Chip size="small" variant="outlined" label={`Cover sheets skipped: ${skippedCount}`} />
            )}
            {droppedCount > 0 && (
              <Chip size="small" color="error" label={`Dropped: ${droppedCount}`} />
            )}
            {failedCount > 0 && (
              <Chip size="small" color="error" variant="outlined" label={`Files failed: ${failedCount}`} />
            )}
            <Typography variant="body2" color="text.secondary">({uploadResults.length} files)</Typography>
            {failedCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<ReplayIcon />}
                onClick={rescanFailed}
                disabled={loading}
              >
                Rescan failed ({failedCount})
              </Button>
            )}
          </Stack>
          {/* A dropped page was scanned and produced nothing — it is NOT in Tickets and nothing else
              in the app will mention it again. Left unnoticed it silently underbills, so it gets a
              warning rather than a chip the eye can slide past. */}
          {droppedCount > 0 && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <strong>{droppedCount} page{droppedCount === 1 ? "" : "s"} produced no ticket.</strong>{" "}
              {droppedCount === 1 ? "It was" : "They were"} scanned but nothing could be read, so{" "}
              {droppedCount === 1 ? "it is" : "they are"} <em>not</em> in the Tickets list — check the
              page list below and rescan {droppedCount === 1 ? "that page" : "those pages"} before
              invoicing, or {droppedCount === 1 ? "its" : "their"} ticket{droppedCount === 1 ? "" : "s"}{" "}
              will be missing from the invoice.
            </Alert>
          )}
          <List dense>
            {uploadResults.map((r) => (
              <ListItem
                key={r.name}
                secondaryAction={
                  r.status === "FAILED" ? (
                    <Button
                      size="small"
                      startIcon={<ReplayIcon />}
                      onClick={() => rescanOne(r)}
                      disabled={loading || !r.file}
                    >
                      Rescan
                    </Button>
                  ) : null
                }
              >
                <Chip
                  size="small"
                  label={r.status}
                  color={statusColor(r.status)}
                  sx={{ mr: 2, minWidth: 84 }}
                />
                <ListItemText
                  primary={r.name}
                  secondaryTypographyProps={{ component: "div" }}
                  secondary={
                    <>
                      <span>{r.message}</span>
                      {Array.isArray(r.pages) && r.pages.length > 1 && (
                        <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {r.pages.map((p) => (
                            <Chip
                              key={p.page}
                              size="small"
                              variant="outlined"
                              color={pageColor(p.status)}
                              label={`Pg ${p.page}: ${p.detail || p.status}`}
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          ))}
                        </Box>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
          {reviewCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {reviewCount} ticket(s) need attention (unrecognized/missing truck). They were saved and are
              highlighted on the <strong>Tickets</strong> screen — fix the truck to make them billable.
            </Typography>
          )}
          {fieldCount > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {fieldCount} handwritten field ticket(s) were detected and saved for review on the{" "}
              <strong>Field Tickets</strong> screen — verify them there to include them in invoicing/payroll.
            </Typography>
          )}
        </Box>
      )}

      {isScanning && <CameraScanner onCapture={handleScanCapture} />}
    </Box>
  );
};

export default ImageUploader;
